import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { MessageCircle, Check, X, Eye, Clock, Loader2, Edit, AlertTriangle, Filter, Trash2, Upload, FileSpreadsheet, CheckCircle, AlertCircle, Save } from 'lucide-react';
import { extractErrorMessage } from '@/lib/api-errors';

export default function AdminConfessions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [confessions, setConfessions] = useState([]);
  const [categories, setCategories] = useState([]); // [{id, name, ...}]
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedConfession, setSelectedConfession] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('review'); // 'review' (pending) | 'edit' (already moderated)
  const [editedTitle, setEditedTitle] = useState('');
  const [editedContent, setEditedContent] = useState('');
  const [editedCategory, setEditedCategory] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // CSV Import
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [csvAutoApprove, setCsvAutoApprove] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/confessions?status=' + filter);
      setConfessions(res.data);
    } catch (error) {
      toast.error('Erro ao carregar confissões');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/home');
      return;
    }
    loadData();
    loadCategories();
  }, [user, navigate, loadData]);

  const loadCategories = async () => {
    try {
      const res = await api.get('/confessions/categories');
      setCategories(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // non-blocking
    }
  };

  const openReview = (confession) => {
    setSelectedConfession(confession);
    setDialogMode('review');
    setEditedTitle(confession.title || '');
    setEditedContent(confession.content || '');
    setEditedCategory(confession.category || '');
    setDialogOpen(true);
  };

  const openEdit = (confession) => {
    setSelectedConfession(confession);
    setDialogMode('edit');
    setEditedTitle(confession.title || '');
    setEditedContent(confession.content || '');
    setEditedCategory(confession.category || '');
    setDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedConfession) return;
    if (!editedTitle.trim() || !editedContent.trim()) {
      toast.error('Título e conteúdo são obrigatórios');
      return;
    }
    setActionLoading(true);
    try {
      await api.patch('/admin/confessions/' + selectedConfession.id, {
        title: editedTitle,
        content: editedContent,
        category: editedCategory || selectedConfession.category
      });
      toast.success('Confissão atualizada');
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao atualizar'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAction = async (action) => {
    if (!selectedConfession) return;
    setActionLoading(true);
    try {
      if (action === 'approved') {
        const body = {};
        if (editedContent !== selectedConfession.content) body.content = editedContent;
        await api.post('/admin/confessions/' + selectedConfession.id + '/approve', body);
        // If admin changed the category in the same dialog, persist it via PATCH
        if (editedCategory && editedCategory !== selectedConfession.category) {
          await api.patch('/admin/confessions/' + selectedConfession.id, { category: editedCategory });
        }
        toast.success('Confissão aprovada!');
      } else {
        await api.post('/admin/confessions/' + selectedConfession.id + '/reject');
        toast.success('Confissão rejeitada');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao processar'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (confessionId) => {
    if (!confirm('Tem a certeza que deseja eliminar esta confissão permanentemente?')) return;
    try {
      await api.delete('/admin/confessions/' + confessionId);
      toast.success('Confissão eliminada');
      loadData();
    } catch (error) {
      toast.error('Erro ao eliminar confissão');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-PT', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // CSV handlers
  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/admin/confessions/csv-preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCsvPreview(res.data);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao processar CSV'));
    }
  };

  const handleCsvImport = async () => {
    if (!csvPreview?.confessions?.length) return;
    setCsvImporting(true);
    try {
      const res = await api.post('/admin/confessions/csv-import', {
        confessions: csvPreview.confessions,
        auto_approve: csvAutoApprove
      });
      toast.success(res.data.message);
      setCsvPreview(null);
      setShowCsvImport(false);
      setFilter('pending');
      loadData();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro na importação'));
    } finally {
      setCsvImporting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending': return <Badge className="bg-yellow-500/20 text-yellow-400">Pendente</Badge>;
      case 'approved': return <Badge className="bg-green-500/20 text-green-400">Aprovada</Badge>;
      case 'rejected': return <Badge className="bg-red-500/20 text-red-400">Rejeitada</Badge>;
      default: return null;
    }
  };

  const counts = {
    pending: confessions.filter(c => c.status === 'pending').length,
    approved: confessions.filter(c => c.status === 'approved').length,
    rejected: confessions.filter(c => c.status === 'rejected').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-ruby" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-6xl mx-auto px-4 pt-24 pb-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
              <MessageCircle className="w-8 h-8 text-ruby" />
              Moderação de Confissões
            </h1>
            <p className="text-muted-foreground mt-1">Reveja e aprove confissões dos utilizadores</p>
          </div>
          <Button onClick={() => { setShowCsvImport(!showCsvImport); setCsvPreview(null); }} variant={showCsvImport ? 'outline' : 'default'} className={showCsvImport ? 'border-border' : 'bg-ruby hover:bg-ruby-light'} data-testid="confessions-csv-toggle">
            <FileSpreadsheet className="w-4 h-4 mr-2" />{showCsvImport ? 'Voltar' : 'Importar CSV'}
          </Button>
        </div>

        {/* CSV Import Panel */}
        {showCsvImport && (
          <Card className="bg-surface border-border mb-8">
            <CardContent className="p-6">
              {!csvPreview ? (
                <div className="text-center py-8">
                  <FileSpreadsheet className="w-14 h-14 text-ruby/40 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">Importar Confissões via CSV</h3>
                  <p className="text-muted-foreground text-sm mb-1 max-w-md mx-auto">
                    Carregue um CSV com colunas <strong>title</strong> e <strong>content</strong>.
                  </p>
                  <p className="text-xs text-muted-foreground mb-4 max-w-md mx-auto">
                    Coluna opcional: <em>category</em>. As confissões ficam como <strong>pendentes</strong> para revisão.
                  </p>
                  <input type="file" accept=".csv" id="confessions-csv-upload" className="hidden" onChange={handleCsvUpload} />
                  <Button onClick={() => document.getElementById('confessions-csv-upload')?.click()} className="bg-ruby hover:bg-ruby-light px-6" data-testid="confessions-csv-upload-btn">
                    <Upload className="w-4 h-4 mr-2" />Selecionar CSV
                  </Button>
                  <pre className="mt-6 max-w-md mx-auto text-left bg-background border border-border rounded-lg p-3 text-xs text-muted-foreground">
{`title,content,category
"Título","<p>Conteúdo...</p>","Romance"`.trim()}
                  </pre>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-foreground">Pré-visualização</h3>
                    <Button variant="outline" onClick={() => setCsvPreview(null)} size="sm" className="border-border">
                      <X className="w-4 h-4 mr-1" />Cancelar
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-background border border-border rounded-lg p-3 text-center">
                      <p className="text-xl font-bold text-foreground">{csvPreview.total_rows}</p>
                      <p className="text-xs text-muted-foreground">Linhas</p>
                    </div>
                    <div className="bg-background border border-green-500/30 rounded-lg p-3 text-center">
                      <p className="text-xl font-bold text-green-400">{csvPreview.valid}</p>
                      <p className="text-xs text-muted-foreground">Válidas</p>
                    </div>
                    <div className="bg-background border border-red-500/30 rounded-lg p-3 text-center">
                      <p className="text-xl font-bold text-red-400">{csvPreview.errors?.length || 0}</p>
                      <p className="text-xs text-muted-foreground">Erros</p>
                    </div>
                  </div>
                  {csvPreview.errors?.length > 0 && (
                    <div className="mb-4 bg-red-500/5 border border-red-500/20 rounded-lg p-3">
                      <h4 className="text-xs font-medium text-red-400 flex items-center gap-1 mb-1"><AlertCircle className="w-3 h-3" />Erros</h4>
                      {csvPreview.errors.slice(0, 5).map((err, i) => (
                        <p key={i} className="text-xs text-red-300">Linha {err.row}: {err.error}</p>
                      ))}
                      {csvPreview.errors.length > 5 && <p className="text-xs text-red-300">...e mais {csvPreview.errors.length - 5}</p>}
                    </div>
                  )}
                  {csvPreview.confessions?.length > 0 && (
                    <div className="mb-4 space-y-2 max-h-60 overflow-y-auto">
                      {csvPreview.confessions.map((c, i) => (
                        <div key={i} className="flex items-center gap-3 bg-background border border-border rounded-lg p-3">
                          <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-foreground truncate">{c.title}</p>
                            <p className="text-xs text-muted-foreground truncate">{c.preview}</p>
                          </div>
                          <Badge variant="outline" className="border-border text-xs shrink-0">{c.category}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                  {csvPreview.valid > 0 && (
                    <div className="flex items-center justify-between pt-3 border-t border-border">
                      <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                        <input type="checkbox" checked={csvAutoApprove} onChange={(e) => setCsvAutoApprove(e.target.checked)} className="rounded" />
                        Aprovar e publicar automaticamente
                      </label>
                      <Button onClick={handleCsvImport} disabled={csvImporting} className="bg-ruby hover:bg-ruby-light" data-testid="confessions-csv-confirm-btn">
                        {csvImporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                        Importar {csvPreview.valid} Confissões
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card className={'bg-surface border-border cursor-pointer ' + (filter === 'pending' ? 'border-yellow-500' : '')} onClick={() => setFilter('pending')}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pendentes</p>
                <p className="text-2xl font-bold text-yellow-400">{counts.pending}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-400" />
            </CardContent>
          </Card>
          <Card className={'bg-surface border-border cursor-pointer ' + (filter === 'approved' ? 'border-green-500' : '')} onClick={() => setFilter('approved')}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Aprovadas</p>
                <p className="text-2xl font-bold text-green-400">{counts.approved}</p>
              </div>
              <Check className="w-8 h-8 text-green-400" />
            </CardContent>
          </Card>
          <Card className={'bg-surface border-border cursor-pointer ' + (filter === 'rejected' ? 'border-red-500' : '')} onClick={() => setFilter('rejected')}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Rejeitadas</p>
                <p className="text-2xl font-bold text-red-400">{counts.rejected}</p>
              </div>
              <X className="w-8 h-8 text-red-400" />
            </CardContent>
          </Card>
        </div>

        {/* Confessions List */}
        <Card className="bg-surface border-border">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <Filter className="w-5 h-5" />
              {filter === 'pending' ? 'Pendentes de Revisão' : filter === 'approved' ? 'Aprovadas' : 'Rejeitadas'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {confessions.length === 0 ? (
              <div className="text-center py-12">
                <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Nenhuma confissão {filter === 'pending' ? 'pendente' : filter === 'approved' ? 'aprovada' : 'rejeitada'}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {confessions.map((conf) => (
                  <div key={conf.id} className="p-4 bg-white/5 rounded-lg">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-medium text-foreground">{conf.title}</h3>
                          {getStatusBadge(conf.status)}
                          {conf.is_anonymous && <Badge variant="outline" className="border-border text-xs">Anónimo</Badge>}
                          {conf.author_name && (
                            <span className="text-xs text-muted-foreground">por: {conf.author_name}</span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{conf.content}</p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>Por: {conf.author_email}</span>
                          <span>{formatDate(conf.created_at)}</span>
                          {conf.category && <Badge variant="outline" className="border-border text-xs">{conf.category}</Badge>}
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {conf.status === 'pending' && (
                          <Button onClick={() => openReview(conf)} className="bg-ruby hover:bg-ruby-light" size="sm">
                            <Eye className="w-4 h-4 mr-2" />
                            Rever
                          </Button>
                        )}
                        {conf.status !== 'pending' && (
                          <Button
                            onClick={() => openEdit(conf)}
                            variant="outline"
                            size="sm"
                            className="border-border"
                            data-testid={`edit-confession-${conf.id}`}
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Editar
                          </Button>
                        )}
                        <Button
                          onClick={() => handleDelete(conf.id)}
                          variant="outline"
                          size="sm"
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          data-testid={`delete-confession-${conf.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Review/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-surface border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-foreground">
                {dialogMode === 'edit' ? 'Editar Confissão' : 'Rever Confissão'}
              </DialogTitle>
            </DialogHeader>
            {selectedConfession && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Título</Label>
                  <Input
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    className="bg-background border-border"
                    data-testid="confession-edit-title"
                  />
                </div>

                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span>Autor: <strong className="text-foreground">{selectedConfession.author_name || selectedConfession.author_email || 'Desconhecido'}</strong></span>
                  {selectedConfession.author_email && <span>({selectedConfession.author_email})</span>}
                  {selectedConfession.is_anonymous && <Badge variant="outline" className="border-amber-500/30 text-amber-400 text-xs">Publicado como Anónimo</Badge>}
                </div>

                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Categoria</Label>
                  <Select value={editedCategory || ''} onValueChange={setEditedCategory}>
                    <SelectTrigger className="bg-background border-border" data-testid="confession-edit-category">
                      <SelectValue placeholder="Seleciona uma categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id || cat.name} value={cat.name}>{cat.name}</SelectItem>
                      ))}
                      {/* Preserve current value if it's no longer in the list */}
                      {editedCategory && !categories.some(c => c.name === editedCategory) && (
                        <SelectItem value={editedCategory}>
                          {editedCategory} (categoria removida)
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">
                    Conteúdo {dialogMode === 'review' && '(pode editar antes de aprovar)'}
                  </Label>
                  <Textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="bg-background border-border min-h-[200px]"
                    data-testid="confession-edit-content"
                  />
                </div>

                {editedContent !== selectedConfession.content && (
                  <div className="flex items-center gap-2 text-yellow-400 text-sm">
                    <AlertTriangle className="w-4 h-4" />
                    O conteúdo foi editado
                  </div>
                )}
              </div>
            )}
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border">
                Cancelar
              </Button>
              {dialogMode === 'review' ? (
                <>
                  <Button variant="destructive" onClick={() => handleAction('rejected')} disabled={actionLoading}>
                    <X className="w-4 h-4 mr-2" />
                    Rejeitar
                  </Button>
                  <Button onClick={() => handleAction('approved')} disabled={actionLoading} className="bg-green-600 hover:bg-green-700">
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                    Aprovar
                  </Button>
                </>
              ) : (
                <Button onClick={handleSaveEdit} disabled={actionLoading} className="bg-ruby hover:bg-ruby-light" data-testid="confession-edit-save">
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar Alterações
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
